<script setup lang="ts">
import { useFetch, useRuntimeConfig } from '#app'
import { useAuthStore } from '~/layers/cms/app/stores/authStore'
import type { CmsCustomComponentType, PageSettings } from '~/layers/cms/app/types/cmsTypes'
import { onMounted, computed, ref } from 'vue'
import BlogCard from '~/components/BlogCard.vue'
import { getNewsList } from '#imports'

const config = useRuntimeConfig()
const { data } = await useFetch(`${config.public.apiBase}/api/get-news-list`, {
  transform: response => (Array.isArray(response) ? (response as PageSettings[]) : []),
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})
const authStore = useAuthStore()
const newsList = ref<PageSettings[]>(data.value ?? [])

onMounted(async () => {
  if (authStore.authorized) {
    const res = await getNewsList()
    if (res.status_ok) {
      newsList.value = res.data as PageSettings[]
    }
  }
})
const aktualnosci = computed<PageSettings[]>(() => newsList.value ?? [])

const shownCount = ref<number>(8)

const showMore = () => {
  shownCount.value += 8
}

const shownAktualnosci = computed<PageSettings[]>(() => {
  return aktualnosci.value.slice(0, shownCount.value)
})

const aktualnosciRows = computed(() => {
  const items = shownAktualnosci.value
  const rows = []

  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2))
  }

  return rows
})

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Special/BlogList'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <div
      v-if="aktualnosciRows && aktualnosciRows.length > 0"
      :id="component_values?.id?.value"
    >
      <TransitionGroup
        v-for="(row, index) in aktualnosciRows"
        :key="JSON.stringify(row)"
        name="news"
        tag="div"
        :class="index % 2 === 0 ? 'bg-background-1' : 'bg-white'"
        class="grid md:grid-cols-1 xl:grid-cols-2 grid-rows-1 gap-14 px-xl xl:px-4xl py-20"
      >
        <BlogCard
          v-for="aktualnosc_chunk in row"
          :key="JSON.stringify(aktualnosc_chunk)"
          :image="(aktualnosc_chunk.customData.articleImage as string)"
          :date="(aktualnosc_chunk.customData.articleDate as string)"
          :title="(aktualnosc_chunk.customData.articleTitle as string)"
          :description="(aktualnosc_chunk.customData.articleDescription as string)"
          :slug="aktualnosc_chunk.slug"
          class="news-card"
          :style="{ animationDelay: `${index * 250}ms` }"
        />
      </TransitionGroup>
    </div>
    <div
      v-else
      class="h-screen flex justify-center py-20"
    >
      <span class="text-H3">Brak Aktualności</span>
    </div>

    <div
      v-if="aktualnosciRows && aktualnosciRows.length > 0 && aktualnosci > shownAktualnosci"
      class="flex items-center justify-center py-20"
    >
      <AppButton
        button-theme="Transparent-Teksty"
        :submit="true"
        @click="showMore"
      >
        <span class="font-bold">DOŁADUJ WIĘCEJ</span>
      </AppButton>
    </div>
  </CmsCustomComponent>
</template>
